// components/models/Seedream4_5ControlPanel.tsx
import React, { memo, useCallback } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import RangeControl from '../ui/RangeControl';
import { Seedream4_5Params } from '#types/types';
import Select from '../ui/Select';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';

export const getDefaultSeedream4_5Params = (): Seedream4_5Params => ({
  prompt: '',
  seed: -1,
  outputMegaPixels: 16,
  mask_alpha_radius: 12
});

const RESOLUTION_OPTIONS = ['2K', '4K'];
const RESOLUTION_SELECT_STYLE: React.CSSProperties = { width: '100%' };

const Seedream4_5ControlPanel: React.FC = () => {
  const { kernel } = useKernel();
  const params = useKernelStore(
    state => state.modelParams[state.diffusionModel] as Seedream4_5Params | undefined
  );

  const handleParamChange = useCallback((
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const { name, value, type } = e.target;

    const newValue = type === 'range' || type === 'number' ? parseFloat(String(value)) : value;

    const diffusionModel = kernel.current?.getDiffusionModel();
    if (!diffusionModel) return;

    kernel.current?.setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Seedream4_5ControlPanel] handleParamChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: newValue,
        }
      };
    });

    if (name === 'prompt') {
      kernel.current?.logUserAction('Setting Changed', {
        setting_name: name,
        value_length: (newValue as string).length,
        model_name: diffusionModel,
        source_component: 'Seedream4_5ControlPanel'
      });
    } else {
      kernel.current?.logUserAction('Setting Changed', {
        setting_name: name,
        value: newValue,
        model_name: diffusionModel,
        source_component: 'Seedream4_5ControlPanel'
      });
    }
  }, [kernel]);

  const handleResolutionChange = useCallback((value: string) => {
    const mappedMegaPixels = value === '4K' ? 16 : 4;
    const diffusionModel = kernel.current?.getDiffusionModel();
    if (!diffusionModel) return;

    kernel.current?.setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) return prevModelParams;
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          outputMegaPixels: mappedMegaPixels,
        }
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: 'outputMegaPixels',
      value: mappedMegaPixels,
      model_name: diffusionModel,
      source_component: 'Seedream4_5ControlPanel'
    });
  }, [kernel]);

  if (!params) return null;

  const currentResolution = params.outputMegaPixels >= 16 ? '4K' : '2K';

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe what you want to change..."
          description="A detailed description of the desired image."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="Mask Alpha Radius"
          name="mask_alpha_radius"
          min={0} max={80} step={1}
          value={params.mask_alpha_radius}
          onChange={handleParamChange}
          description="How many pixels will be transparent on result borders."
        />
      </div>

      <div className="mb-4">
        <Select
          id="seedreamResolution"
          label="Resolution"
          value={currentResolution}
          options={RESOLUTION_OPTIONS}
          onChange={handleResolutionChange}
          style={RESOLUTION_SELECT_STYLE}
        />
        <p className="text-xs text-[#4D4D4D] mt-1">Available values are limited by the provider for this model.</p>
      </div>

    </>
  );
};

export default memo(Seedream4_5ControlPanel);

